import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpClient } from "../../../services/httpClient";
import { showToast } from "../../../utils/showToast";

const CLAVES = {
  monto:            "bonificacion_monto_default",
  montoExterno:     "bonificacion_monto_comisionista",
  dias:             "bonificacion_dias_vencimiento",
};

export default function ConfiguracionBonificaciones() {
  const [config, setConfig]   = useState({ monto: "", montoExterno: "", dias: "" });
  const [saving, setSaving]   = useState({ monto: false, montoExterno: false, dias: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    httpClient.get("/api/configuracion")
      .then((data) => {
        const map = Object.fromEntries(data.map((r) => [r.clave, r.valor]));
        setConfig({
          monto:        map[CLAVES.monto]        ?? "20.00",
          montoExterno: map[CLAVES.montoExterno] ?? "15.00",
          dias:         map[CLAVES.dias]         ?? "180",
        });
      })
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar la configuración." }))
      .finally(() => setLoading(false));
  }, []);

  const guardar = async (campo, clave, valor) => {
    setSaving((s) => ({ ...s, [campo]: true }));
    try {
      await httpClient.put(`/api/configuracion/${clave}`, { valor });
      showToast({ type: "success", message: "Configuración guardada." });
    } catch {
      showToast({ type: "danger", message: "No se pudo guardar." });
    } finally {
      setSaving((s) => ({ ...s, [campo]: false }));
    }
  };

  return (
    <section className="management-page management-theme-teal configuracion-wrapper">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-center gap-2 mb-1">
            <Link to="/dashboard/configuracion" className="text-muted text-decoration-none small">
              <i className="bi bi-chevron-left me-1"></i>Configuración
            </Link>
          </div>
          <div className="dashboard-eyebrow">Configuración / Bonificaciones</div>
          <h5 className="mb-1">Parámetros de bonificaciones</h5>
          <p className="text-muted mb-0">
            Estos valores se aplican globalmente. Si un comisionista tiene monto individual configurado, ese tiene prioridad.
          </p>
        </div>

        {loading ? (
          <div className="text-muted">Cargando...</div>
        ) : (
          <div className="row g-3">
            {/* Card monto referentes internos */}
            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="config-card-icon">
                      <i className="bi bi-person-check"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold">Monto — referentes internos</h6>
                      <small className="text-muted">Ex-estudiantes, docentes y administrativos</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-3">
                    Bonificación por defecto para referentes vinculados a la institución (ex-alumnos, docentes, administrativos) cuando no tienen monto individual configurado.
                  </p>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      step="0.01"
                      value={config.monto}
                      onChange={(e) => setConfig((c) => ({ ...c, monto: e.target.value }))}
                    />
                    <button
                      className="btn management-btn-primary"
                      disabled={saving.monto}
                      onClick={() => guardar("monto", CLAVES.monto, config.monto)}
                    >
                      {saving.monto ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Card monto comisionistas externos */}
            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="config-card-icon">
                      <i className="bi bi-person-badge"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold">Monto — comisionistas externos</h6>
                      <small className="text-muted">Comisionistas y externos</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-3">
                    Bonificación por defecto para comisionistas o externos sin vínculo laboral con la institución, cuando no tienen monto individual configurado.
                  </p>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      step="0.01"
                      value={config.montoExterno}
                      onChange={(e) => setConfig((c) => ({ ...c, montoExterno: e.target.value }))}
                    />
                    <button
                      className="btn management-btn-primary"
                      disabled={saving.montoExterno}
                      onClick={() => guardar("montoExterno", CLAVES.montoExterno, config.montoExterno)}
                    >
                      {saving.montoExterno ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Card días de vencimiento */}
            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="config-card-icon">
                      <i className="bi bi-clock-history"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold">Días para vencimiento</h6>
                      <small className="text-muted">Días desde que la matrícula se paga</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-3">
                    Si el comisionista no cobra su bono en este período, el sistema lo anula automáticamente.
                  </p>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      step="1"
                      value={config.dias}
                      onChange={(e) => setConfig((c) => ({ ...c, dias: e.target.value }))}
                    />
                    <span className="input-group-text">días</span>
                    <button
                      className="btn management-btn-primary"
                      disabled={saving.dias}
                      onClick={() => guardar("dias", CLAVES.dias, config.dias)}
                    >
                      {saving.dias ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
